import { Component, Input, Output, EventEmitter, OnInit, SimpleChanges, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DEFAULT_TOGGLE_CONFIG, ToggleConfig } from './toggle.modal';

@Component({
  selector: 'ds-toggel',
  templateUrl: './toggel.component.html',
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggelComponent),
      multi: true
    }
  ]
})
export class ToggelComponent implements OnInit, ControlValueAccessor {
  public _toggleInput: ToggleConfig = { ...DEFAULT_TOGGLE_CONFIG };
  @Input() toggleInput!: ToggleConfig;

  /** Only set once a [formControl]/[ngModel] binding has written to us. Callers that
   *  drive the toggle purely through [toggleInput] never trip this, so their behaviour
   *  is unchanged. */
  private isFormBound = false;
  private value = false;
  /** Non-null once Angular forms has told us the control's disabled state; from then on
   *  the form owns it, not the incoming config. */
  private formDisabled: boolean | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.toggleInput) {
      const val: ToggleConfig = changes.toggleInput.currentValue || {};
      const preservedId = val.id || this.toggleInput?.id;
      this.toggleInput = { ...DEFAULT_TOGGLE_CONFIG, ...val };
      // A replaced config must not clobber the state the form control owns.
      if (this.isFormBound) {
        this.toggleInput.isActive = this.value;
      }
      if (this.formDisabled !== null) {
        this.toggleInput.disabled = this.formDisabled;
      }
      this.toggleInput.id = preservedId || `ds-toggel-${Math.random().toString(36).slice(2, 9)}`;
    }
  }
  @Output() isActiveChange = new EventEmitter<boolean>();

  ngOnInit(): void {
    if (!this.toggleInput?.id) {
      this.toggleInput.id = `ds-toggel-${Math.random().toString(36).slice(2, 9)}`;
    }
  }

  onToggle(ev: Event) {
    if (this.toggleInput?.disabled) return;
    const input = ev.target as HTMLInputElement;
    this.value = input.checked;
    this.toggleInput.isActive = this.value;
    this.onChange(this.value);
    this.onTouched();
    this.isActiveChange.emit(this.value);
  }

  onChange = (_: any) => { };
  onTouched = () => { };

  writeValue(value: any): void {
    this.isFormBound = true;
    this.value = !!value;
    this.toggleInput = { ...(this.toggleInput ?? DEFAULT_TOGGLE_CONFIG), isActive: this.value };
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.toggleInput = { ...(this.toggleInput ?? DEFAULT_TOGGLE_CONFIG), disabled: isDisabled };
  }
}
